import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface AcademicInstitution {
  id: string;
  institution_name: string;
  domain: string | null;
  institution_type: string | null;
  headquarters_state: string | null;
  headquarters_country: string | null;
  headquarters_city: string | null;
  region: string | null;
  territory: string | null;
  sales_rep: string | null;
  carr_modality: string | null;
  icp_tier: string | null;
  icp_score: number | null;
  life_science_id: string | null;
  parent_company_id: string | null;
  parent_name: string | null;
  is_main_institution: boolean;
  institution_hierarchy_level: number | null;
  hub_institution_id: string | null;
  created_at: string | null;
  child_count: number;
  lab_count: number;
  pi_count: number;
  trial_count: number;
  grant_count: number;
  contact_count: number;
}

export interface AcademicLab {
  lab_id: string;
  lab_name: string;
  principal_investigator: string | null;
  research_focus: string | null;
  department: string | null;
  lab_website: string | null;
  contact_email: string | null;
  contact_status: string | null;
  relevance_score: number | null;
  research_areas: string[] | null;
  therapeutic_areas: string[] | null;
  key_technologies: string[] | null;
  institution_id: string | null;
  institution_name: string | null;
  main_institution_id: string | null;
  main_institution_name: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  discovery_source: string | null;
  discovery_date: string | null;
}

export interface AcademicPI {
  id: string;
  full_name: string;
  first_name: string | null;
  last_name: string | null;
  department: string | null;
  role: string | null;
  specialty: string | null;
  email: string | null;
  orcid: string | null;
  google_scholar_url: string | null;
  lab_website_url: string | null;
  research_areas: string[] | null;
  therapeutic_focus: string[] | null;
  key_technologies: string[] | null;
  source: string | null;
  enrichment_status: string;
  enriched_at: string | null;
  organization_name: string | null;
  organization_city: string | null;
  organization_state: string | null;
  institution_id: string | null;
  institution_name: string | null;
  institution_domain: string | null;
  lab_id: string | null;
  lab_name: string | null;
  lab_website: string | null;
  lab_research_focus: string | null;
}

export interface AcademicFilters {
  searchTerm?: string;
  institutionTypes?: string[];
  states?: string[];
  countries?: string[];
  territories?: string[];
  salesReps?: string[];
  hasTrials?: boolean;
  hasGrants?: boolean;
  hasContacts?: boolean;
  parentOnly?: boolean;
  limit?: number;
  offset?: number;
}

export interface AcademicStats {
  total: number;
  totalLabs: number;
  totalPIs: number;
  withTrials: number;
  withGrants: number;
  withContacts: number;
  byType: { type: string; count: number }[];
}

export function useAcademicMarket(filters: AcademicFilters) {
  return useQuery({
    queryKey: ['academic-market', filters],
    queryFn: async () => {
      let query = (supabase.from('v_academic_market') as any)
        .select('*', { count: 'exact' });

      if (filters.searchTerm?.trim()) {
        query = query.or(
          `institution_name.ilike.%${filters.searchTerm.trim()}%,domain.ilike.%${filters.searchTerm.trim()}%`
        );
      }
      if (filters.institutionTypes?.length) {
        query = query.in('institution_type', filters.institutionTypes);
      }
      if (filters.states?.length) {
        query = query.in('headquarters_state', filters.states);
      }
      if (filters.countries?.length) {
        query = query.in('headquarters_country', filters.countries);
      }
      if (filters.territories?.length) {
        query = query.in('territory', filters.territories);
      }
      if (filters.salesReps?.length) {
        query = query.in('sales_rep', filters.salesReps);
      }
      if (filters.hasTrials === true) {
        query = query.gt('trial_count', 0);
      }
      if (filters.hasGrants === true) {
        query = query.gt('grant_count', 0);
      }
      if (filters.hasContacts === true) {
        query = query.gt('contact_count', 0);
      }
      if (filters.parentOnly === true) {
        query = query.eq('is_main_institution', true);
      }

      query = query
        .order('trial_count', { ascending: false, nullsFirst: false })
        .range(filters.offset || 0, (filters.offset || 0) + (filters.limit || 50) - 1);

      const { data, error, count } = await query;
      if (error) throw error;
      return {
        data: (data || []) as AcademicInstitution[],
        totalCount: count || 0,
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useAcademicStats() {
  return useQuery({
    queryKey: ['academic-market-stats'],
    queryFn: async () => {
      const { data, error } = await (supabase.from('v_academic_market') as any)
        .select('institution_type, trial_count, grant_count, contact_count, lab_count, pi_count');

      if (error) throw error;

      const rows = (data || []) as { institution_type: string | null; trial_count: number; grant_count: number; contact_count: number; lab_count: number; pi_count: number }[];

      const typeMap = new Map<string, number>();
      let withTrials = 0;
      let withGrants = 0;
      let withContacts = 0;
      let totalLabs = 0;
      let totalPIs = 0;

      rows.forEach((r) => {
        const t = r.institution_type || 'Unknown';
        typeMap.set(t, (typeMap.get(t) || 0) + 1);
        if (r.trial_count > 0) withTrials++;
        if (r.grant_count > 0) withGrants++;
        if (r.contact_count > 0) withContacts++;
        totalLabs += r.lab_count || 0;
        totalPIs += r.pi_count || 0;
      });

      const byType = Array.from(typeMap.entries())
        .map(([type, count]) => ({ type, count }))
        .sort((a, b) => b.count - a.count);

      return {
        total: rows.length,
        totalLabs,
        totalPIs,
        withTrials,
        withGrants,
        withContacts,
        byType,
      } as AcademicStats;
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useAcademicFilterOptions() {
  return useQuery({
    queryKey: ['academic-market-filter-options'],
    queryFn: async () => {
      const { data, error } = await (supabase.from('v_academic_market') as any)
        .select('institution_type, headquarters_state, headquarters_country, territory, sales_rep');

      if (error) throw error;

      const rows = (data || []) as { institution_type: string | null; headquarters_state: string | null; headquarters_country: string | null; territory: string | null; sales_rep: string | null }[];

      const institutionTypes = [...new Set(rows.map(r => r.institution_type).filter(Boolean))].sort() as string[];
      const states = [...new Set(rows.map(r => r.headquarters_state).filter(Boolean))].sort() as string[];
      const countries = [...new Set(rows.map(r => r.headquarters_country).filter(Boolean))].sort() as string[];
      const territories = [...new Set(rows.map(r => r.territory).filter(Boolean))].sort() as string[];
      const salesReps = [...new Set(rows.map(r => r.sales_rep).filter(Boolean))].sort() as string[];

      return { institutionTypes, states, countries, territories, salesReps };
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useAcademicLabs(companyId: string | undefined) {
  return useQuery({
    queryKey: ['academic-labs', companyId],
    queryFn: async () => {
      const { data, error } = await (supabase.from('v_labs_with_hierarchy') as any)
        .select('*')
        .or(`institution_id.eq.${companyId},main_institution_id.eq.${companyId}`)
        .order('lab_name');

      if (error) throw error;
      return (data || []) as AcademicLab[];
    },
    enabled: !!companyId,
    staleTime: 10 * 60 * 1000,
  });
}

export function useAcademicPIs(companyId: string | undefined) {
  return useQuery({
    queryKey: ['academic-pis', companyId],
    queryFn: async () => {
      const { data, error } = await (supabase.from('v_academic_pis_list') as any)
        .select('*')
        .eq('institution_id', companyId)
        .order('full_name');

      if (error) throw error;
      return (data || []) as AcademicPI[];
    },
    enabled: !!companyId,
    staleTime: 10 * 60 * 1000,
  });
}

export function useChildInstitutions(companyId: string | undefined) {
  return useQuery({
    queryKey: ['academic-children', companyId],
    queryFn: async () => {
      const { data, error } = await (supabase.from('v_academic_hierarchy') as any)
        .select('*')
        .eq('parent_id', companyId)
        .order('institution_name');

      if (error) throw error;
      return (data || []) as AcademicInstitution[];
    },
    enabled: !!companyId,
    staleTime: 10 * 60 * 1000,
  });
}
